<template>
  <DModal :show-close="false" :isBgOverlay="true" :placeInTop="true" @close="$emit('close')">
    <div class="d-modal" slot="insertComponent">
      <div class="d-modal__header">
        <h2>Opret klassefravær</h2>
      </div>
      <div class="d-modal__content">
        <template v-if="isMultipleSchoolsActive && !activeSchoolId">
          <div class="d-group">
            <label class="d-label">Vælg skole for klassefravær</label>
            <select v-model="activeSchoolId" @change="onChangeActiveSchoolId">
              <option v-for="school in userSchoolsSorted" :value="school.id">
                {{ school.name }}
              </option>
            </select>
          </div>
        </template>
        <template v-else-if="activeSchoolId && (isLoadingSchoolCalendars || isLoadingSchoolYears)">
          <div class="do-spinner__container">
            <div class="do-spinner do-spinner--large"></div>
          </div>
        </template>
        <template v-else>
          <div class="d-group">
            <label class="d-label">Vælg klasse</label>
            <DSelectCals :items="klassCalendars"
                         :itemsSelected="selectedKlasses"
                         :multiple="false"
                         :placeholder="$t('vue2.dselect.placeholder_calendars')"
                         :showCalendarYear="true"
                         @update="setSelectedKlasses">
            </DSelectCals>
          </div>
          <template v-if="selectedKlasses.length > 0">
            <OutOfSchoolSubject
              :schoolId="activeSchoolId"
              :calendarId="selectedKlasses[0]['id']"
              :date="startDate"
              :selectedSubject="selectedSubject"
              @setSubject="setSelectedSubject">
            </OutOfSchoolSubject>
          </template>
          <div class="d-group">
            <label class="d-label">Deltagende personer</label>
            <DSelectCals :items="userCalendars"
                         :itemsSelected="selectedUsers"
                         :multiple="true"
                         :placeholder="$t('vue2.dselect.placeholder_calendars')"
                         @update="setSelectedUsers">
            </DSelectCals>
          </div>
          <template v-if="selectedUsers.length > 0">
            <OutOfSchoolAbsence
              @setSubject="setSelectedAbsence">
            </OutOfSchoolAbsence>
          </template>

          <div class="d-group">
            <label class="d-label">Dato</label>
            <DatepickerRange
              :allowSameDate="true"
              :startdate="date"
              @startchanged="setStartDate"
              @endchanged="setEndDate">
            </DatepickerRange>
          </div>
          <div class="time-changer__group">
            <label class="d-label">Tid</label>
            <TimeChanger
              :showIcon="true"
              :start="startTime"
              :end="endTime"
              :isFiveMinutesInterval="false"
              @updateStart="setStartTime"
              @updateEnd="setEndTime">
            </TimeChanger>
          </div>
        </template>
      </div>
      <div class="d-modal__buttons d-modal__buttons__padding" style="padding: 1rem 3rem;">
        <button class="do-bttn" @click="$emit('close')">{{ $t('vue2.common.cancel') }}</button>
        <button class="do-bttn do-bttn--submit do-bttn--submit--disabled"
                v-if="!isValidated">
          {{ $t('vue2.common.create') }}
        </button>
        <button class="do-bttn do-bttn--submit" :class="{'do-bttn--submit--loading': isSubmitting}" v-else
                @click="submit">{{ $t('vue2.common.create') }}
        </button>
      </div>
    </div>
  </DModal>
</template>

<script>
import docendo from '../../../docendo'
import DModal from '../../common/DModal'
import eventbus from '../../../eventbus'
import DSelectCals from '../../DSelect/DSelectCals.vue'
import {mapGetters} from 'vuex'
import moment from 'moment'
import DatepickerRange from '../../datepicker/DatePickerRange.vue'
import TimeChanger from '../../TimeChanger.vue'
import OutOfSchoolSubject from './OutOfSchoolSubject.vue'
import OutOfSchoolAbsence from './OutOfSchoolAbsence.vue'

export default {
  components: {OutOfSchoolAbsence, OutOfSchoolSubject, DatepickerRange, TimeChanger, DSelectCals, DModal},
  props: {
    initDate: {
      default: moment()
    }
  },
  data () {
    return {
      date: moment(),
      startDate: moment(),
      endDate: moment(),
      startTime: moment().set({'hour': 8, 'minutes': 0}),
      endTime: moment().set({'hour': 16, 'minutes': 0}),
      selectedKlasses: [],
      selectedSubject: null,
      selectedUsers: [],
      selectedAbsence: null,
      events: [],
      isSubmitting: false,
      isKlassEventCreated: false,
      isUserAbsenceCreated: false,
      isAllUserEventsCreated: false,
      activeSchoolId: null,
      isMultipleSchoolsActive: false,
      isLoadingSchoolCalendars: false,
      schoolCalendars: [],
      schoolYears: [],
      isLoadingSchoolYears: false,
      availableCalendarId: null,
      availableSubjectId: null,
      isAllEventsUpdated: false
    }
  },
  computed: {
    ...mapGetters([
      'userCalendars',
      'schoolId'
    ]),
    activeSchoolYear () {
      return this.schoolYears.find(year =>
        moment(this.startDate).isBetween(year.student_time_start, year.student_time_end, null, '[]')
      )
    },
    klassCalendars () {
      const activeSchoolYearId = this.activeSchoolYear ? this.activeSchoolYear.id : null
      return this.schoolCalendars
        .filter(calendar => calendar.type === 'klass' && calendar.school_year_id === activeSchoolYearId)
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    user () {
      return this.$store.state.user
    },
    userSchools () {
      return this.user && this.user.schools ? this.user.schools : []
    },
    userSchoolsSorted () {
      return this.userSchools.sort((a, b) => a.name.localeCompare(b.name))
    },
    isValidated () {
      return this.selectedKlasses.length > 0 && this.selectedSubject && (this.selectedUsers.length === 0 || this.selectedAbsence)
    },
    dbEventStart () {
      const date = moment(this.startDate).format('YYYY-MM-DD')
      const time = this.startTime.locale('LT')
      return moment(date).set({
        hour: time.get('hour'),
        minutes: time.get('minutes')
      })
    },
    dbEventEnd () {
      const date = moment(this.endDate).format('YYYY-MM-DD')
      const time = this.endTime.locale('LT')
      return moment(date).set({
        hour: time.get('hour'),
        minutes: time.get('minutes')
      })
    }
  },
  methods: {
    onChangeActiveSchoolId () {
      this.readSchoolCalendars()
      this.readSchoolYears()
      this.$nextTick(_ => {
        console.log(document.getElementById('app'))
      })
    },
    readSchoolCalendars () {
      this.isLoadingSchoolCalendars = true
      docendo.calendarsForSchool(this.activeSchoolId, data => {
        this.schoolCalendars = data
        this.isLoadingSchoolCalendars = false
      })
    },
    readSchoolYears () {
      this.isLoadingSchoolYears = true
      docendo.schoolYearsRead(this.activeSchoolId, {}, 'name', data => {
        this.schoolYears = data
        this.isLoadingSchoolYears = false
      })
    },
    setSelectedSubject (subject) {
      this.selectedSubject = subject
    },
    setStartDate (payload) {
      this.startDate = payload
    },
    setEndDate (payload) {
      this.endDate = payload
    },
    setStartTime (payload) {
      this.startTime = payload
    },
    setEndTime (payload) {
      this.endTime = payload
    },
    setSelectedKlasses (val) {
      this.selectedKlasses = val
      this.setSelectedSubject(null)
    },
    setSelectedUsers (val) {
      this.selectedUsers = val
    },
    setSelectedAbsence (absence) {
      this.selectedAbsence = absence
    },
    submit () {
      this.isSubmitting = true

      // Reset state flags
      this.isKlassEventCreated = false
      this.isUserAbsenceCreated = false
      this.isAllUserEventsCreated = false
      this.isAllEventsUpdated = false

      // Start the submission process
      this.readKlassEvents(() => {
        if (this.isKlassEventCreated) {
          this.createAbsence(() => {
            if (this.isUserAbsenceCreated) {
              this.createMainEvent(() => {
                if (this.isAllUserEventsCreated) {
                  this.updateEvents(() => {
                    if (this.isAllEventsUpdated) {
                      this.isSubmitting = false
                      this.showToast(this.$t('vue2.create_activity.created'), true)
                      this.$emit('onCreatedEvents')
                      this.$emit('close')
                    }
                  })
                }
              })
            }
          })
        }
      })
    },
    readKlassEvents (callback) {
      const calendarIds = this.selectedKlasses.map(calendar => calendar.id)
      docendo.calendarEvents2(calendarIds, this.dbEventStart, this.dbEventEnd, null, success => {
        this.events = success && success.length > 0 ? JSON.parse(success) : []
        this.isKlassEventCreated = true // Mark klass events as created
        this.findEventsWithUsersNotParticipating()

        callback()
      }, failure => {
        this.showToast('Error fetching class events', false)
        this.isSubmitting = false
      })
    },
    // Create absence and set flag when completed
    createAbsence (callback) {
      const schoolId = this.selectedSubject.school_id
      const absenceStart = moment(this.startDate).format('YYYY-MM-DD')
      const absenceEnd = moment(this.endDate).format('YYYY-MM-DD')

      let completedCount = 0
      const totalUsers = this.selectedUsers.length
      if (totalUsers === 0) {
        this.isUserAbsenceCreated = true // Mark absence as created when all are done
        callback()
        return
      }
      this.selectedUsers.forEach(userCalendar => {
        const userCalendarId = userCalendar.id
        docendo.schoolEventSerialsCreate(
          schoolId,
          userCalendarId,
          this.selectedAbsence.id,
          absenceStart,
          absenceEnd,
          this.startTime.toISOString(),
          this.endTime.toISOString(),
          false,
          '',
          data => {
            completedCount++
            if (completedCount === totalUsers) {
              this.isUserAbsenceCreated = true // Mark absence as created when all are done
              // this.showToast('Oprettet fravær', true)
              callback()
            }
          },
          failure => {
            this.showToast('Error creating absence', false)
            this.isSubmitting = false
          }
        )
      })
    },
    // Create the main event after creating klass events and absence
    createMainEvent (callback) {
      const schoolId = this.selectedSubject.school_id
      const calendarId = this.selectedSubject.calendar_id
      const calendarIds = [this.selectedSubject.calendar_id, ...this.selectedUsers.map(calendar => calendar.id)]
      const subjectId = this.selectedSubject.id
      const internalNote = ''

      docendo.createEvent(schoolId, calendarId, calendarIds, subjectId, this.dbEventStart, this.dbEventEnd, '', '', '', internalNote, data => {
        if (data && data.status === 'error') {
          this.showToast(data.message, false)
          this.$emit('close')
          return
        }
        this.isAllUserEventsCreated = true // Mark all user events as created
        callback()
      }, failure => {
        this.showToast('Something went wrong', false)
      })
    },

    findEventsWithUsersNotParticipating () {
      const participatingCalendarIds = this.selectedUsers.map(calendar => calendar.id)

      // Find events, hvor der er mindst én bruger, som IKKE deltager
      const eventsWithUsersNotParticipating = this.events.filter(event =>
        event.calendars.some(calendarEvent =>
          calendarEvent.calendar_type === 'user' &&
          !participatingCalendarIds.includes(calendarEvent.calendar_id)
        )
      )
      this.createAvailableEventsOnEachUserNotParticipating(eventsWithUsersNotParticipating)
    },
    // Create events for each user not participating in filtered events
    createAvailableEventsOnEachUserNotParticipating (events) {
      const participatingCalendarIds = this.selectedUsers.map(calendar => calendar.id)

      events.forEach(event => {
        event.calendars.forEach(calendarEvent => {
          if (calendarEvent.calendar_type === 'user' && !participatingCalendarIds.includes(calendarEvent.calendar_id)) {
            const schoolId = this.schoolId
            const calendarId = this.availableCalendarId
            const subjectId = this.availableSubjectId
            const calendarIds = [this.availableCalendarId, calendarEvent.calendar_id]
            const eventStart = event.start
            const eventEnd = event.end
            this.createEvent(schoolId, calendarId, calendarIds, subjectId, eventStart, eventEnd)
          }
        })
      })
    },

    // Create a single event and provide a callback for completion
    createEvent (schoolId, calendarId, calendarIds, subjectId, eventStart, eventEnd, callback) {
      docendo.createEvent(
        schoolId,
        calendarId,
        calendarIds,
        subjectId,
        eventStart,
        eventEnd,
        '',
        '',
        '',
        '',
        data => {
          if (data && data.status === 'error') {
            this.showToast(data.message, false)
            return
          }
          callback && callback() // Notify that the event was successfully created
        },
        failure => {
          this.showToast('Something went wrong', false)
        }
      )
    },
    updateEvents (callback) {
      const eventsToUpdate = this.events.map(lesson => {
        const userCalendarIds = lesson.calendars
          .filter(calEvent => calEvent.calendar_type === 'user')
          .map(calEvent => calEvent.calendar_id)

        let statusData = lesson.substitution_status ? JSON.parse(lesson.substitution_status) : {}

        userCalendarIds.forEach(calId => {
          statusData[calId] = { status: 1 }
        })

        return {
          school_id: lesson.school_id, // Sørger for at inkludere school_id til grouping
          event_id: lesson.event_id,
          substitution_status: JSON.stringify(statusData)
        }
      })

      // Funktion til at gruppere events efter school_id
      const groupedEvents = eventsToUpdate.reduce((groups, event) => {
        if (!groups[event.school_id]) {
          groups[event.school_id] = []
        }
        groups[event.school_id].push(event)
        return groups
      }, {})

      const schoolIds = Object.keys(groupedEvents)
      let completedUpdates = 0

      // Kalder docendo.updateEvents for hver skole
      schoolIds.forEach(schoolId => {
        docendo.updateEvents(schoolId, groupedEvents[schoolId], () => {
          completedUpdates++
          if (completedUpdates === schoolIds.length) {
            this.isAllEventsUpdated = true
            callback && callback()
          }
        })
      })
    },
    // Utility method to show toast notifications
    showToast (message, success) {
      eventbus.toast = {text: message, success}
    }
  },
  beforeMount () {
    this.date = this.initDate
    const schoolConfig = {
      7272: {
        activeSchoolId: 7272,
        availableCalendarId: 394554,
        availableSubjectId: 1009095,
        isMultipleSchoolsActive: true,
        callOnChange: true
      },
      60494: {
        isMultipleSchoolsActive: true,
        availableCalendarId: 394635,
        availableSubjectId: 1009370,
        callOnChange: false
      },
      60598: {
        activeSchoolId: 60598,
        availableCalendarId: 394721,
        availableSubjectId: 1009904,
        isMultipleSchoolsActive: false,
        callOnChange: true
      },
      60032: {
        activeSchoolId: 60032,
        availableCalendarId: 401122,
        availableSubjectId: 1032466,
        isMultipleSchoolsActive: false,
        callOnChange: true
      },
      36305: {
        activeSchoolId: 36305,
        availableCalendarId: 401122,
        availableSubjectId: 1056241,
        isMultipleSchoolsActive: false,
        callOnChange: true
      },
      36203: {
        activeSchoolId: 36203,
        availableCalendarId: 409125,
        availableSubjectId: 1056243,
        isMultipleSchoolsActive: false,
        callOnChange: true
      },
      61476: {
        activeSchoolId: 61476,
        availableCalendarId: 412237,
        availableSubjectId: 1069030,
        isMultipleSchoolsActive: false,
        callOnChange: true
      },
      62676: {
        activeSchoolId: 62676,
        availableCalendarId: 432097,
        availableSubjectId: 1161807,
        isMultipleSchoolsActive: false,
        callOnChange: true
      },
      60095: {
        activeSchoolId: 60095,
        availableCalendarId: 443810,
        availableSubjectId: 1230500,
        isMultipleSchoolsActive: false,
        callOnChange: true
      },
      62000: {
        activeSchoolId: 62000,
        availableCalendarId: 444666,
        availableSubjectId: 1235081,
        isMultipleSchoolsActive: false,
        callOnChange: true
      },
      62810: {
        activeSchoolId: 62810,
        availableCalendarId: 444739,
        availableSubjectId: 1235950,
        isMultipleSchoolsActive: false,
        callOnChange: true
      },
      62084: {
        activeSchoolId: 62084,
        availableCalendarId: 407478,
        availableSubjectId: 1051420,
        isMultipleSchoolsActive: false,
        callOnChange: true
      },
      62765: {
        activeSchoolId: 62765,
        availableCalendarId: 446970,
        availableSubjectId: 1243003,
        isMultipleSchoolsActive: false,
        callOnChange: true
      },
      63076: {
        activeSchoolId: 63076,
        availableCalendarId: 450816,
        availableSubjectId: 1258584,
        isMultipleSchoolsActive: false,
        callOnChange: true
      }
    }

    const config = schoolConfig[this.schoolId]
    if (config) {
      if (config.activeSchoolId) {
        this.activeSchoolId = config.activeSchoolId
      }

      this.availableCalendarId = config.availableCalendarId
      this.availableSubjectId = config.availableSubjectId
      this.isMultipleSchoolsActive = config.isMultipleSchoolsActive || false

      // Call onChangeActiveSchoolId only if the config specifies to do so
      if (config.callOnChange) {
        this.onChangeActiveSchoolId()
      }
    }
  }
}
</script>

<style scoped>
.d-modal {
  width: 550px;
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
}

.d-modal__close {
  position: absolute;
  top: 5px;
  right: 5px;
  font-size: 16px !important;
}

.d-modal__buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.d-modal__content {
  padding: 1rem 3rem;
  overflow-y: auto;
  max-height: calc(100vh - 2rem - 52px - 60px);
}

.d-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.do-spinner__container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
}
</style>



// WEBPACK FOOTER //
// OutOfSchool.vue?38d4a64e